import { FC, useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import { apiGetBestWrestlerListStats, apiGetBestWrestlerWomanListStats } from '../components/ui_components/ApiList';
import Pagination from '../components/stats_components/Pagination';
import { LoadingState, ErrorState } from '../components/stats_components/LoadingState';
import ContainerDetailStats from '../components/details_stats/ContainerDetailStats';
import BestWrestlerCard from '../components/best_wrestler_components/BestWrestlerCard';
import HeaderMostFightWinsComponent from '../components/most_fights_wins_components/HeaderMostFightWinsComponent';

interface WrestlerStats {
    id: number;
    name: string;
    associated_counts_belts_array?: unknown[];
}

interface PaginationInfo {
    last_page: number;
    current_page: number;
    total: number;
}

interface BestWrestlerProps {
    isWoman?: boolean;
}

const BestWrestler: FC<BestWrestlerProps> = ({ isWoman = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [allWrestlers, setAllWrestlers] = useState<WrestlerStats[]>([]);
    const [filteredWrestlers, setFilteredWrestlers] = useState<WrestlerStats[]>([]);
    const [wrestlers, setWrestlers] = useState<WrestlerStats[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();
    const currentPage = Number(searchParams.get('page')) || 1;
    const year = searchParams.get('year') || 'globale';
    const itemsPerPage = 10;
    const loadedYearRef = useRef<string | null>(null);
    const loadedIsWomanRef = useRef<boolean | null>(null);
    const prevYearRef = useRef<string | null>(null);
    const prevIsWomanRef = useRef<boolean | null>(null);

    const currentYear = new Date().getFullYear();
    const years: (string | number)[] = ['globale', ...Array.from({ length: currentYear - 2004 }, (_, i) => currentYear - i)];

    const navigateToWrestler = (wrestler: WrestlerStats): void => {
        const wrestlerId = wrestler.id;
        const wrestlerName = wrestler.name;
        navigate(`/wrestlers/i_${wrestlerId}_d_${wrestlerName}`);
    };

    const updatePagination = useCallback((wrestlersToPaginate: WrestlerStats[], page: number): void => {
        if (!wrestlersToPaginate || wrestlersToPaginate.length === 0) {
            setWrestlers([]);
            setPagination({
                last_page: 1,
                current_page: 1,
                total: 0
            });
            return;
        }

        const totalPages = Math.ceil(wrestlersToPaginate.length / itemsPerPage);
        const validPage = Math.max(1, Math.min(page, totalPages));

        if (validPage !== page) {
            setSearchParams({ page: String(validPage), ...(year && { year }) });
            return;
        }

        const startIndex = (validPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageWrestlers = wrestlersToPaginate.slice(startIndex, endIndex);

        setWrestlers(pageWrestlers);
        setPagination({
            last_page: totalPages,
            current_page: validPage,
            total: wrestlersToPaginate.length
        });
    }, [year, itemsPerPage, setSearchParams]);

    const filterWrestlers = (searchText: string): void => {
        if (!searchText || searchText.length === 0) {
            setFilteredWrestlers(allWrestlers);
            setSearchParams({ page: '1', ...(year && { year }) });
            return;
        }

        if (searchText.length < 3) {
            setFilteredWrestlers(allWrestlers);
            setSearchParams({ page: '1', ...(year && { year }) });
            return;
        }

        const filtered = allWrestlers.filter(wrestler =>
            wrestler.name.toLowerCase().includes(searchText.toLowerCase())
        );

        setFilteredWrestlers(filtered);
        setSearchParams({ page: '1', ...(year && { year }) });
    };

    const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const searchText = e.target.value;
        setSearchInput(searchText);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchText.length === 0) {
            filterWrestlers('');
            return;
        }

        if (searchText.length < 3) {
            filterWrestlers('');
            return;
        }

        const timeout = setTimeout(() => {
            filterWrestlers(searchText);
        }, 300);

        searchTimeoutRef.current = timeout;
    };

    useEffect(() => {
        const yearChanged = prevYearRef.current !== null && prevYearRef.current !== year;
        const isWomanChanged = prevIsWomanRef.current !== null && prevIsWomanRef.current !== isWoman;
        const isFirstLoad = prevYearRef.current === null;

        if (!isFirstLoad && !yearChanged && !isWomanChanged) {
            return;
        }

        if (loadedYearRef.current === year && loadedIsWomanRef.current === isWoman) {
            prevYearRef.current = year;
            prevIsWomanRef.current = isWoman;
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();

        const fetchAllWrestlers = async (): Promise<void> => {
            setIsLoading(true);
            try {
                const response = (isWoman
                    ? await apiGetBestWrestlerWomanListStats(1, year, '', true)
                    : await apiGetBestWrestlerListStats(1, year, '', true)) as unknown as { success?: boolean; data?: WrestlerStats[] };

                if (controller.signal.aborted) return;

                if (response.success && response.data) {
                    setAllWrestlers(response.data);
                    setFilteredWrestlers(response.data);
                    loadedYearRef.current = year;
                    loadedIsWomanRef.current = isWoman;
                    prevYearRef.current = year;
                    prevIsWomanRef.current = isWoman;
                    if (yearChanged) {
                        setSearchParams({ page: '1', ...(year && { year }) });
                    }
                } else {
                    setError('Failed to load wrestlers');
                }
            } catch (err) {
                if (err instanceof Error && err.name !== 'AbortError') {
                    setError('Error loading wrestlers');
                    console.error('Error:', err);
                }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        fetchAllWrestlers();
        return () => controller.abort();
    }, [year, isWoman]);

    useEffect(() => {
        updatePagination(filteredWrestlers, currentPage);
    }, [currentPage, filteredWrestlers, updatePagination]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handlePageChange = (newPage: number): void => {
        setSearchParams({ page: String(newPage), ...(year && { year }) });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        setSearchParams({ page: '1', year: e.target.value });
        setSearchInput('');
    };

    if (isLoading) {
        return <LoadingState />;
    }

    if (error) {
        return <ErrorState error={error} />;
    }

    return (
        <CommonWrapper className="wrapper-best-wrestler">
            <ContainerDetailStats className="container-detail-stats">
                <HeaderMostFightWinsComponent
                    year={year}
                    handleYearChange={handleYearChange}
                    isLoading={isLoading}
                    searchInput={searchInput}
                    handleSearchInput={handleSearchInput}
                    years={years}
                    title={isWoman ? 'Lottatrici Migliori' : 'Lottatori Migliori'}
                    currentPage={currentPage}
                    lastPage={pagination?.last_page || 1}
                    onPageChange={handlePageChange}
                />

                <div className="wrestlers-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {wrestlers.map((wrestler) => (
                        <BestWrestlerCard
                            key={wrestler.id}
                            wrestlerStats={wrestler as any}
                            beltFights={(wrestler.associated_counts_belts_array || []) as any}
                            navigateToWrestler={navigateToWrestler as any}
                        />
                    ))}
                </div>

                {pagination && (
                    <Pagination
                        currentPage={currentPage}
                        lastPage={pagination.last_page}
                        onPageChange={handlePageChange}
                    />
                )}
            </ContainerDetailStats>
        </CommonWrapper>
    );
};


export default BestWrestler;
